/**
 * Where a time-scoped widget's window picker sits, and which widgets have one (#1674). Kept apart from the renderer so
 * the decision is plain data a test reads.
 */

import type { WidgetInstance } from "./types";

/**
 * Where a widget's window picker sits: "overlay" floats over the widget's top right corner (view mode); "row" is a row
 * of its own under the edit-mode drag strip, so the picker covers neither the strip nor the delete and info buttons
 * (#1674); "none" for a widget with nothing to window (stub mode, a KPI, a widget bound to no tag).
 */
export type RangePickerPlacement = "none" | "overlay" | "row";

export function rangePickerPlacement(w: {
  live: boolean;
  editMode: boolean;
  type: WidgetInstance["type"];
  dataSource: WidgetInstance["dataSource"];
}): RangePickerPlacement {
  if (!w.live || !TIME_RANGED_WIDGET_TYPES.has(w.type) || !widgetHasLiveBinding(w.dataSource)) return "none";
  return w.editMode ? "row" : "overlay";
}

/**
 * Widget types whose live rendering is scoped to a time window — the charts
 * that plot `series`, plus the table (which flips to a per-bucket history view
 * when a range is picked). KPI / gauge headline the latest value, so a window
 * picker would be noise there (the widened default still fills their sparkline).
 * Nor is the SPC tile: it draws the server's control chart over the chart's
 * own window (the last 30 days of the tag), so a picker would change nothing.
 */
const TIME_RANGED_WIDGET_TYPES: ReadonlySet<WidgetInstance["type"]> = new Set([
  "timeseries",
  "table",
  "histogram",
  "scatter",
]);

/** True when the widget binds at least one tag/port key (something to window). */
function widgetHasLiveBinding(ds: WidgetInstance["dataSource"]): boolean {
  if (!ds || (ds.kind !== "tag" && ds.kind !== "port") || !ds.slots) return false;
  return Object.values(ds.slots).some((keys) => keys.length > 0);
}
